import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui";
import { ComboboxProps } from "./types";

const Combobox: React.FC<ComboboxProps> = ({
  placeholder,
  searchPlaceholder,
  noDataPlaceholder,
  options,
  value,
  onSelect,
  disabled,
  className,
  multiSelect = false,
}) => {
  const [open, setOpen] = React.useState(false);

  // Display values
  const displayLabel = () => {
    if (multiSelect && Array.isArray(value)) {
      const labels = value
        .map((val) => options.find((opt) => opt.value === val)?.label)
        .filter(Boolean);
      return labels.length ? (
        labels.join(", ")
      ) : (
        <span className="text-muted-foreground">{placeholder}</span>
      );
    }

    if (!multiSelect) {
      return (
        options.find((opt) => opt.value == value)?.label || (
          <span className="text-muted-foreground">{placeholder}</span>
        )
      );
    }

    return placeholder;
  };

  const handleSelect = (selectedValue: string) => {
    if (multiSelect) {
      const currentValues = Array.isArray(value) ? [...value] : [];
      const isSelected = currentValues.includes(selectedValue);
      const updated = isSelected
        ? currentValues.filter((v) => v !== selectedValue)
        : [...currentValues, selectedValue];
      onSelect(updated as any);
    } else {
      onSelect(selectedValue);
      setOpen(false); // Close only for single-select
    }
  };

  const isSelected = (val: string) => {
    return multiSelect && Array.isArray(value)
      ? value.includes(val)
      : value === val;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        asChild
        disabled={disabled}
        className={cn(
          "h-[40px] w-full bg-transparent rounded-[4px] hover:bg-transparent",
          className
        )}
      >
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between text-[#2b333f] font-normal"
        >
          <span
            className={cn("truncate", value ? "text-[#2b333f]" : "text-g-400")}
          >
            {displayLabel()}
          </span>
          <ChevronDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-h-40">
        <Command>
          <CommandInput
            placeholder={searchPlaceholder || "Search..."}
            className="h-5"
          />
          <CommandList>
            <CommandEmpty>{noDataPlaceholder || "No data found"}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => handleSelect(opt.value)}
                  disabled={opt.disabled}
                >
                  {opt.label}
                  <Check
                    className={cn(
                      "ml-auto h-4 w-4",
                      isSelected(opt.value) ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default Combobox;
